import React, { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { 
  Trophy, Flame, Users, Globe, Building2, Crown, 
  Medal, Award, TrendingUp, Target, ChevronLeft,
  Loader2, AlertTriangle, RefreshCw
} from 'lucide-react';
import { Link } from 'react-router-dom';
import learningStreakService, { LeaderboardFilters, StreakLeaderboardItemDto } from '@/services/learning-streak.service';
import StreakWidget from '@/components/learner/StreakWidget';

export default function StreakLeaderboard() {
  const [scope, setScope] = useState<'global' | 'department'>('global');
  const [refreshKey, setRefreshKey] = useState(0);

  const { data: leaderboard, isLoading, error, refetch } = useQuery({
    queryKey: ['streak-leaderboard', scope, refreshKey],
    queryFn: () => learningStreakService.getLeaderboard({ scope, limit: 50 }),
    staleTime: 5 * 60 * 1000, // 5 minutes
    refetchInterval: 30 * 60 * 1000 // Refetch every 30 minutes
  });

  const getRankIcon = (rank: number) => {
    switch (rank) {
      case 1:
        return <Crown className="w-6 h-6 text-yellow-500" />;
      case 2:
        return <Medal className="w-6 h-6 text-gray-400" />;
      case 3:
        return <Award className="w-6 h-6 text-amber-600" />;
      default:
        return <span className="w-6 h-6 flex items-center justify-center text-sm font-bold text-gray-500">#{rank}</span>;
    }
  };

  const getRankBadgeColor = (rank: number) => {
    switch (rank) {
      case 1:
        return 'bg-gradient-to-r from-yellow-400 to-yellow-600';
      case 2:
        return 'bg-gradient-to-r from-gray-400 to-gray-600';
      case 3:
        return 'bg-gradient-to-r from-amber-400 to-amber-600';
      default:
        return 'bg-gradient-to-r from-blue-500 to-purple-600';
    }
  };

  const handleRefresh = () => {
    setRefreshKey(prev => prev + 1);
    refetch();
  };

  const getAvatar = (user: StreakLeaderboardItemDto) => {
    if (user.avatar_url) {
      return (
        <img 
          src={user.avatar_url} 
          alt={user.user_name}
          className="w-12 h-12 rounded-full object-cover"
        />
      );
    }
    
    // Generate avatar from initials
    const initials = user.user_name
      .split(' ')
      .map(name => name.charAt(0))
      .slice(0, 2)
      .join('')
      .toUpperCase();
    
    return (
      <div className="w-12 h-12 rounded-full bg-gradient-to-br from-blue-500 to-purple-600 flex items-center justify-center text-white font-semibold">
        {initials}
      </div>
    );
  };

  if (isLoading) {
    return (
      <div className="space-y-6">
        <div className="flex items-center justify-between">
          <Link
            to="/learn"
            className="flex items-center gap-2 text-gray-600 hover:text-gray-900 transition-colors"
          >
            <ChevronLeft className="w-5 h-5" />
            Back to Dashboard
          </Link>
        </div>

        <div className="bg-white rounded-lg border border-gray-200 p-8">
          <div className="flex items-center justify-center">
            <Loader2 className="w-8 h-8 animate-spin text-blue-600" />
            <span className="ml-3 text-gray-600">Loading leaderboard...</span>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="space-y-6">
        <div className="flex items-center justify-between">
          <Link
            to="/learn"
            className="flex items-center gap-2 text-gray-600 hover:text-gray-900 transition-colors"
          >
            <ChevronLeft className="w-5 h-5" />
            Back to Dashboard
          </Link>
        </div>

        <div className="bg-white rounded-lg border border-gray-200 p-8">
          <div className="text-center">
            <AlertTriangle className="w-12 h-12 text-red-500 mx-auto mb-4" />
            <h3 className="text-lg font-semibold text-gray-900 mb-2">Failed to Load Leaderboard</h3>
            <p className="text-gray-600 mb-4">Unable to fetch the streak leaderboard data.</p>
            <button
              onClick={handleRefresh}
              className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 flex items-center gap-2 mx-auto"
            >
              <RefreshCw className="w-4 h-4" />
              Try Again
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <Link
            to="/learn"
            className="flex items-center gap-2 text-gray-600 hover:text-gray-900 transition-colors"
          >
            <ChevronLeft className="w-5 h-5" />
            Back to Dashboard
          </Link>
          <div>
            <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
              <Trophy className="w-7 h-7 text-yellow-500" />
              Streak Leaderboard
            </h1>
            <p className="text-gray-600">See who's leading the learning streak challenge</p>
          </div>
        </div>
        <button
          onClick={handleRefresh}
          disabled={isLoading}
          className="flex items-center gap-2 px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors"
        >
          <RefreshCw className={`w-4 h-4 ${isLoading ? 'animate-spin' : ''}`} />
          Refresh
        </button>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Leaderboard - 2 columns */}
        <div className="lg:col-span-2">
          <div className="bg-white rounded-lg border border-gray-200">
            {/* Scope Selector */}
            <div className="p-4 border-b border-gray-200">
              <div className="flex items-center gap-4">
                <h2 className="text-lg font-semibold text-gray-900">Rankings</h2>
                <div className="flex bg-gray-100 rounded-lg p-1">
                  <button
                    onClick={() => setScope('global')}
                    className={`px-3 py-1.5 text-sm font-medium rounded-md transition-all flex items-center gap-2 ${
                      scope === 'global'
                        ? 'bg-white text-blue-600 shadow-sm'
                        : 'text-gray-600 hover:text-gray-900'
                    }`}
                  >
                    <Globe className="w-4 h-4" />
                    Global
                  </button>
                  <button
                    onClick={() => setScope('department')}
                    className={`px-3 py-1.5 text-sm font-medium rounded-md transition-all flex items-center gap-2 ${
                      scope === 'department'
                        ? 'bg-white text-blue-600 shadow-sm'
                        : 'text-gray-600 hover:text-gray-900'
                    }`}
                  >
                    <Building2 className="w-4 h-4" />
                    Department
                  </button>
                </div>
              </div>
            </div>

            {/* Leaderboard List */}
            <div className="p-4">
              {leaderboard && leaderboard.length > 0 ? (
                <div className="space-y-3">
                  {leaderboard.map((user) => (
                    <div 
                      key={user.user_id}
                      className={`flex items-center gap-4 p-4 rounded-lg border transition-all hover:shadow-md ${
                        user.rank <= 3 ? getRankBadgeColor(user.rank) + ' text-white' : 'bg-gray-50 hover:bg-gray-100'
                      }`}
                    >
                      {/* Rank */}
                      <div className="flex-shrink-0">
                        {getRankIcon(user.rank)}
                      </div>

                      {/* Avatar */}
                      <div className="flex-shrink-0">
                        {getAvatar(user)}
                      </div>

                      {/* User Info */}
                      <div className="flex-1 min-w-0">
                        <h3 className={`font-semibold truncate ${user.rank <= 3 ? 'text-white' : 'text-gray-900'}`}>
                          {user.user_name}
                        </h3>
                        <div className={`flex items-center gap-4 text-sm ${user.rank <= 3 ? 'text-white/90' : 'text-gray-500'}`}>
                          <span className="flex items-center gap-1">
                            <Target className="w-3 h-3" />
                            {user.total_learning_days} days total
                          </span>
                          <span className="flex items-center gap-1">
                            <TrendingUp className="w-3 h-3" />
                            {user.longest_streak} longest
                          </span>
                        </div>
                      </div>

                      {/* Current Streak */}
                      <div className="flex-shrink-0 text-right">
                        <div className={`text-2xl font-bold flex items-center gap-1 ${user.rank <= 3 ? 'text-white' : 'text-gray-900'}`}>
                          {user.current_streak}
                          <Flame className={`w-5 h-5 ${user.rank <= 3 ? 'text-orange-200' : 'text-orange-500'}`} />
                        </div>
                        <p className={`text-xs ${user.rank <= 3 ? 'text-white/90' : 'text-gray-500'}`}>
                          current streak
                        </p>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-8">
                  <Users className="w-12 h-12 text-gray-300 mx-auto mb-3" />
                  <p className="text-gray-600">No leaderboard data available</p>
                  <p className="text-sm text-gray-500 mt-1">Start learning to see rankings!</p>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Right Sidebar */}
        <div className="space-y-6">
          {/* Your Streak */}
          <StreakWidget showActions={false} />

          {/* Leaderboard Stats */}
          <div className="bg-gradient-to-br from-purple-50 to-indigo-50 rounded-lg p-4">
            <h3 className="font-semibold text-gray-900 mb-3 flex items-center gap-2">
              <Trophy className="w-5 h-5 text-purple-600" />
              Competition Stats
            </h3>
            {leaderboard && leaderboard.length > 0 && (
              <div className="space-y-3">
                <div className="flex items-center justify-between">
                  <span className="text-sm text-gray-600">Top Streak</span>
                  <span className="text-sm font-medium text-gray-900 flex items-center gap-1">
                    {Math.max(...leaderboard.map(u => u.current_streak))}
                    <Flame className="w-4 h-4 text-orange-500" />
                  </span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-sm text-gray-600">Active Learners</span>
                  <span className="text-sm font-medium text-gray-900">
                    {leaderboard.length}
                  </span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-sm text-gray-600">Avg. Streak</span>
                  <span className="text-sm font-medium text-gray-900">
                    {Math.round(leaderboard.reduce((sum, u) => sum + u.current_streak, 0) / leaderboard.length)}
                  </span>
                </div>
              </div>
            )}
          </div>

          {/* Milestones Info */}
          <div className="bg-white rounded-lg border border-gray-200 p-4">
            <h3 className="font-semibold text-gray-900 mb-3 flex items-center gap-2">
              <Award className="w-5 h-5 text-blue-600" />
              Streak Milestones
            </h3>
            <div className="space-y-2">
              {[
                { days: 7, label: '1 Week', color: 'text-green-600 bg-green-50' },
                { days: 14, label: '2 Weeks', color: 'text-blue-600 bg-blue-50' },
                { days: 30, label: '1 Month', color: 'text-purple-600 bg-purple-50' },
                { days: 50, label: '50 Days', color: 'text-orange-600 bg-orange-50' },
                { days: 100, label: '100 Days', color: 'text-red-600 bg-red-50' },
                { days: 365, label: '1 Year', color: 'text-yellow-600 bg-yellow-50' }
              ].map((milestone) => (
                <div key={milestone.days} className="flex items-center justify-between">
                  <span className="text-sm text-gray-600">{milestone.label}</span>
                  <span className={`text-xs font-medium px-2 py-1 rounded-full ${milestone.color}`}>
                    {milestone.days} days
                  </span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}